import {myEnrollmentsApi} from '@/api/my-enrollments.api.js'
import {useFlashMessages} from '@/composables/use-flash-messages.js'
import {enrollmentStatuses} from '@/constants/enrollments.js'
import {ref} from 'vue'

export function useResumeEnrollment(enrollment) {
  const {setFlashSuccess, setFlashFailure} = useFlashMessages()

  //We send start_or_stop_enrollment with direction 'resume' so the server treats it as a
  //directional, idempotent resume rather than a blind toggle: resuming an already-active
  //enrollment is a no-op. A typed RESUME is already resumed server-side by the messages
  //endpoint, so without this the follow-up toggle would re-pause it.
  //
  //We still coalesce concurrent callers: if two RESUME messages arrive before the first API
  //call finishes, we store the in-flight promise here so the next caller waits for it instead
  //of firing a second request. Once it settles we clear the ref so a future RESUME starts fresh.
  const resumeEffort = ref(null)

  const executeResume = async () => {
    try {
      const json = await myEnrollmentsApi.startOrStopEnrollment(enrollment.value.id, 'resume')
      enrollment.value.status = json.enrollment.status
      enrollment.value.pausedAt = json.enrollment.pausedAt
    }
    finally {
      resumeEffort.value = null
    }
  }

  const resumeEnrollment = async () => {
    if (!enrollment.value?.isPaused) {
      return false
    }
    const inFlight = resumeEffort.value
    if (inFlight) {
      await inFlight.catch(() => {})
      return false
    }
    const effort = executeResume()
    resumeEffort.value = effort
    try {
      await effort
      if (enrollment.value?.status === enrollmentStatuses.PAUSED) {
        setFlashFailure(`Couldn't resume the course. Please try again.`)
        return false
      }
      setFlashSuccess('Your enrollment has been resumed!')
      return true
    }
    catch {
      setFlashFailure(`Couldn't resume the course. Please try again.`)
      return false
    }
  }

  return {resumeEnrollment}
}
